import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DocumentNeedToggle from './DocumentNeedToggle';
import { newJobDocument, neededToApply } from '../lib/jobDocuments';
import DOMPurify from 'dompurify';
import { supabase } from '../lib/supabase';
import api, { UPLOAD_TIMEOUT } from '../lib/api';
import { compressImage } from '../lib/imageCompression';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import RichTextEditor from './RichTextEditor';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { toast } from 'sonner';
import { BOHOL_MUNICIPALITIES } from '../data/boholLocations';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Checkbox } from './ui/checkbox';
import { WORKER_SKILLS, COMMON_BENEFITS } from '../lib/constants';
import { usePricing } from '../hooks/usePricing';
import {
  Sparkles,
  ArrowLeft,
  ArrowRight,
  Loader2,
  RefreshCw,
  Check,
  Briefcase,
  MapPin,
  GraduationCap,
  Wallet,
  Gift,
  FileText,
  Star,
  Image as ImageIcon,
  Upload,
  X,
  Edit,
  Save,
  Plus,
  Paperclip,
  AlertTriangle
} from 'lucide-react';

/**
 * AI Job Wizard - Step-by-step questionnaire for AI-powered job post creation
 */
const AIJobWizard = ({ categories, onComplete, onCancel, onSaveDraft, draftId, autoSaving, initialDraftData }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [generatedPost, setGeneratedPost] = useState(null);
  const [jobImage, setJobImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imagePage, setImagePage] = useState(1);
  const [customImage, setCustomImage] = useState(null); // Custom uploaded image URL
  const [uploadingImage, setUploadingImage] = useState(false);
  const [posting, setPosting] = useState(false); // Loading state for posting job
  const [editingDescription, setEditingDescription] = useState(false); // Edit mode for description
  const [editedDescription, setEditedDescription] = useState(''); // Edited description content
  const [editingRequirements, setEditingRequirements] = useState(false); // Edit mode for requirements
  const [editedRequirements, setEditedRequirements] = useState(''); // Edited requirements (one per line)
  const [editedTitle, setEditedTitle] = useState(''); // Editable AI-generated title
  const [imagePosition, setImagePosition] = useState(50); // Image vertical crop position (0-100)
  const [descriptionLength, setDescriptionLength] = useState('concise'); // 'concise' or 'detailed'
  const [isFeatured, setIsFeatured] = useState(false);
  const [credits, setCredits] = useState(0);
  const { pricing } = usePricing();
  const fileInputRef = React.useRef(null);
  const isSubmittingRef = React.useRef(false); // Ref to prevent double submissions (state can have race conditions)

  // Fetch credits balance for featured option
  useEffect(() => {
    let retries = 0;
    const fetchCredits = async () => {
      try {
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.access_token) {
          // Session not ready yet — retry up to 3 times
          if (retries < 3) {
            retries++;
            setTimeout(fetchCredits, 1000);
          }
          return;
        }
        const response = await api.get('/api/credits/balance', {
          headers: { Authorization: `Bearer ${session.session.access_token}` }
        });
        setCredits(response.data.balance || 0);
      } catch (error) {
        console.error('Failed to fetch credits:', error);
      }
    };
    fetchCredits();
  }, []);

  const [formData, setFormData] = useState({
    // Step 1: Basics
    job_title: '',
    category: '',
    category_id: '',
    custom_category: '', // For "Other" category custom input
    locations: [],

    // Step 2: Requirements
    // Simplified: position_type replaces both employment_type and work_schedule
    // 'full_time' = permanent full-time position
    // 'part_time' = permanent part-time position
    position_type: 'full_time',
    experience_level: 'none',
    education_level: 'any',
    skills: [],
    specific_requirements: '', // Specific requirements like "must own motorcycle"

    // Step 3: Compensation
    salary_min: '',
    salary_max: '',
    salary_period: 'monthly',
    benefits: [],

    // Step 4: Additional
    additional_notes: '',
    urgent_hiring: false,
    no_experience_needed: false,
    with_housing: false,
    custom_question: '',  // Optional screening question
    required_documents: [],  // Documents required from applicants
    positions_available: 1  // Number of positions to fill
  });

  // Restore wizard state from draft data when resuming
  useEffect(() => {
    if (!initialDraftData) return;
    const d = initialDraftData;
    setFormData(prev => ({
      ...prev,
      job_title: d.title || '',
      category_id: d.category_id || '',
      category: categories.find(c => c.id === d.category_id)?.name || '',
      locations: d.locations || [],
      position_type: d.work_schedule || 'full_time',
      salary_min: d.salary_min || '',
      salary_max: d.salary_max || '',
      salary_period: d.salary_period || 'monthly',
      benefits: d.benefits || [],
      urgent_hiring: d.urgent_hiring || false,
      no_experience_needed: d.no_experience_needed || false,
      with_housing: d.with_housing || false,
      custom_question: d.custom_question || '',
      required_documents: d.required_documents || [],
      positions_available: d.positions_available || 1,
    }));
    // If description exists, set as generated post and jump to preview
    if (d.description) {
      setGeneratedPost({
        generated_title: d.title || '',
        full_description: d.description,
        requirements: typeof d.requirements === 'string'
          ? d.requirements.split('\n').filter(Boolean)
          : d.requirements || [],
      });
      setEditedTitle(d.title || '');
      setEditedDescription(d.description);
      if (d.image_url) {
        setCustomImage(d.image_url);
      }
      setImagePosition(d.image_position || 50);
      if (!d._wizardState) {
        setCurrentStep(4); // Jump to preview step (default when no wizard state saved)
      }
    }
    // Restore wizard-specific state (skills, step, etc.) from localStorage
    if (d._wizardState) {
      const ws = d._wizardState;
      setFormData(prev => ({
        ...prev,
        skills: ws.skills || [],
        experience_level: ws.experience_level || 'none',
        education_level: ws.education_level || 'any',
        specific_requirements: ws.specific_requirements || '',
        additional_notes: ws.additional_notes || '',
      }));
      if (typeof ws.currentStep === 'number') {
        setCurrentStep(ws.currentStep);
      }
    }
  }, [initialDraftData, categories]);

  // Build partial job data from current wizard state for draft saving
  const buildDraftData = () => {
    const salaryMin = parseInt(formData.salary_min) || 0;
    const salaryMax = parseInt(formData.salary_max) || 0;
    return {
      title: editedTitle || generatedPost?.generated_title || formData.job_title || '',
      description: editedDescription || generatedPost?.full_description || '',
      category_id: formData.category_id || null,
      locations: formData.locations || [],
      type: 'permanent',
      work_schedule: formData.position_type || 'full_time',
      salary_min: salaryMin,
      salary_max: salaryMax,
      salary_period: formData.salary_period || 'monthly',
      requirements: editedRequirements
        ? editedRequirements.split('\n').map(r => r.trim()).filter(r => r)
        : (Array.isArray(generatedPost?.requirements) ? generatedPost.requirements : []),
      benefits: formData.benefits || [],
      image_url: customImage || jobImage?.image_url || null,
      image_position: imagePosition,
      urgent_hiring: formData.urgent_hiring || false,
      no_experience_needed: formData.no_experience_needed || formData.experience_level === 'none',
      with_housing: formData.with_housing || formData.benefits?.includes('Free Accommodation') || false,
      custom_question: formData.custom_question || null,
      required_documents: formData.required_documents || [],
      positions_available: formData.positions_available || 1,
      post_type: 'job',
      _wizardState: {
        currentStep,
        skills: formData.skills || [],
        experience_level: formData.experience_level || 'none',
        education_level: formData.education_level || 'any',
        specific_requirements: formData.specific_requirements || '',
        additional_notes: formData.additional_notes || '',
      }
    };
  };

  const handleSaveDraft = () => {
    if (!onSaveDraft) return;
    const draftData = buildDraftData();
    if (!draftData.title?.trim()) {
      toast.error('Please enter a job title first');
      return;
    }
    onSaveDraft(draftData);
  };

  // Auto-save every 30s when there's a title
  React.useEffect(() => {
    if (!onSaveDraft || !formData.job_title?.trim()) return;
    const timer = setTimeout(() => {
      const draftData = buildDraftData();
      if (draftData.title?.trim()) onSaveDraft(draftData);
    }, 30000);
    return () => clearTimeout(timer);
  }, [formData, currentStep, editedTitle, editedDescription, editedRequirements, generatedPost, customImage, jobImage, imagePosition]); // Auto-save deps

  // Scroll to top of wizard when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Wizard steps configuration with translations
  const steps = [
    { id: 'basics', title: t('post.aiWizard.stepBasics', 'Job Basics'), icon: Briefcase, description: t('post.aiWizard.whatPosition', 'What position are you hiring for?') },
    { id: 'requirements', title: t('post.aiWizard.stepRequirements', 'Requirements'), icon: GraduationCap, description: t('post.aiWizard.skillsNeeded', 'What skills and experience do you need?') },
    { id: 'compensation', title: t('post.aiWizard.stepCompensation', 'Compensation'), icon: Wallet, description: t('post.aiWizard.benefitsPerks', 'What are you offering?') },
    { id: 'notes', title: t('post.aiWizard.stepNotes', 'Additional Info'), icon: FileText, description: t('post.aiWizard.additionalInfo', 'Any other details?') },
    { id: 'preview', title: t('post.aiWizard.stepPreview', 'Preview & Edit'), icon: Sparkles, description: t('post.aiWizard.generatedDescription', 'Review your AI-generated post') }
  ];

  const handleCategoryChange = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    const isOther = category?.name?.toLowerCase().includes('other');
    setFormData(prev => ({
      ...prev,
      category_id: categoryId,
      category: category?.name || '',
      // Clear custom category if not selecting "Other"
      custom_category: isOther ? prev.custom_category : ''
    }));
  };

  const toggleSkill = (skillValue) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skillValue)
        ? prev.skills.filter(s => s !== skillValue)
        : [...prev.skills, skillValue]
    }));
  };

  const toggleBenefit = (benefitId) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.includes(benefitId)
        ? prev.benefits.filter(b => b !== benefitId)
        : [...prev.benefits, benefitId]
    }));
  };

  const getValidationError = () => {
    switch (currentStep) {
      case 0: { // Basics
        if (!formData.job_title?.trim()) return 'Please enter a job title';
        if (!formData.category_id) return 'Please select a category';
        const selectedCategory = categories.find(c => c.id === formData.category_id);
        const isOtherCategory = selectedCategory?.name?.toLowerCase().includes('other');
        if (isOtherCategory && !formData.custom_category.trim()) return 'Please enter a custom category name';
        if (!formData.locations || formData.locations.length === 0) return 'Please select at least one location';
        return null;
      }
      case 1: // Requirements
        if (!formData.position_type) return 'Please select a work schedule (full-time or part-time)';
        return null;
      case 2: { // Compensation
        const minVal = parseFloat(formData.salary_min);
        const maxVal = parseFloat(formData.salary_max);
        if (!formData.salary_min || isNaN(minVal) || minVal <= 0) return 'Please enter a valid minimum salary (must be greater than 0)';
        if (!formData.salary_max || isNaN(maxVal) || maxVal <= 0) return 'Please enter a valid maximum salary (must be greater than 0)';
        if (minVal > maxVal) return 'Minimum salary cannot be greater than maximum salary';
        return null;
      }
      case 3: return null; // Optional step
      case 4: return generatedPost ? null : 'Please generate a job post first';
      default: return 'Unknown step';
    }
  };

  const canProceed = () => getValidationError() === null;

  const handleNext = async () => {
    const error = getValidationError();
    if (error) {
      toast.error(error);
      return;
    }
    if (currentStep === 3) {
      // Generate the job post before moving to preview
      await generateJobPost();
    } else if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const generateJobPost = async () => {
    setGenerating(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error(t('common.pleaseLogin', 'Please log in'));
        return;
      }

      // Convert skill values to labels for AI
      const skillLabels = formData.skills.map(skillValue => {
        const skill = WORKER_SKILLS.find(s => s.value === skillValue);
        return skill ? skill.label : skillValue;
      });

      // Use custom_category if "Other" category is selected
      const selectedCategory = categories.find(c => c.id === formData.category_id);
      const isOtherCategory = selectedCategory?.name?.toLowerCase().includes('other');
      const categoryToSend = isOtherCategory && formData.custom_category.trim()
        ? formData.custom_category.trim()
        : formData.category;

      const response = await api.post(
        '/api/ai/generate-job-post',
        {
          job_title: formData.job_title,
          category: categoryToSend,
          location: formData.locations.join(', '),
          // position_type maps to both employment_type and work_schedule (they're now unified)
          employment_type: formData.position_type,
          experience_level: formData.experience_level,
          education_level: formData.education_level,
          skills: skillLabels,
          salary_min: formData.salary_min ? Math.floor(Number(formData.salary_min)) : null,
          salary_max: formData.salary_max ? Math.floor(Number(formData.salary_max)) : null,
          salary_period: formData.salary_period,
          work_schedule: formData.position_type, // Same as employment_type now
          benefits: formData.benefits,
          additional_notes: formData.additional_notes || null,
          specific_requirements: formData.specific_requirements || null,
          required_documents: formData.required_documents.length > 0 ? formData.required_documents : null,
          description_length: descriptionLength
        },
        {
          headers: {
            Authorization: `Bearer ${session.session.access_token}`
          }
        }
      );

      if (response.data.success) {
        setGeneratedPost(response.data.data);
        setEditedTitle(response.data.data.generated_title || ''); // Initialize editable title
        setEditedDescription(''); // Reset edited description on regenerate
        setEditingDescription(false); // Exit edit mode
        setCurrentStep(4);
        // Also fetch an image using the job title
        fetchJobImage(1, formData.job_title);
      } else {
        toast.error(t('post.aiWizard.generateFailed', 'Failed to generate job post'));
      }
    } catch (error) {
      console.error('Error generating job post:', error);
      toast.error(error.response?.data?.detail || t('post.aiWizard.generateFailed', 'Failed to generate job post'));
    } finally {
      setGenerating(false);
    }
  };

  const fetchJobImage = async (page = 1, jobTitle = null) => {
    setImageLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const response = await api.get('/api/ai/job-image', {
        params: {
          category: formData.category,
          page,
          job_title: jobTitle || formData.job_title
        },
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });

      if (response.data.success && response.data.data.image_url) {
        setJobImage(response.data.data);
        setImagePage(page);
      }
    } catch (error) {
      console.error('Error fetching job image:', error);
    } finally {
      setImageLoading(false);
    }
  };

  const refreshImage = () => {
    fetchJobImage(imagePage + 1, formData.job_title);
  };

  // Handle custom image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('post.aiWizard.invalidImageType', 'Please select an image file'));
      return;
    }

    // Validate file size (20MB max - compression will bring it down)
    if (file.size > 20 * 1024 * 1024) {
      toast.error(t('post.aiWizard.imageTooLarge', 'Image must be less than 20MB'));
      return;
    }

    setUploadingImage(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error(t('common.pleaseLogin', 'Please log in'));
        return;
      }

      // Compress before uploading
      const compressed = await compressImage(file, 'jobImage');

      const formDataUpload = new FormData();
      formDataUpload.append('file', compressed);

      const response = await api.post('/api/upload-image', formDataUpload, {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        },
        timeout: UPLOAD_TIMEOUT,
      });

      if (response.data.success && response.data.image_url) {
        setCustomImage(response.data.image_url);
        toast.success(t('post.aiWizard.imageUploaded', 'Image uploaded successfully'));
      } else {
        toast.error(t('post.aiWizard.uploadFailed', 'Failed to upload image'));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(error.response?.data?.detail || t('post.aiWizard.uploadFailed', 'Failed to upload image'));
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove custom image and revert to AI-generated
  const removeCustomImage = () => {
    setCustomImage(null);
  };

  const handleComplete = async () => {
    // Prevent double submission using ref (synchronous check prevents race conditions)
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setPosting(true);

    // Validate that we have generated content
    if (!generatedPost || !generatedPost.full_description) {
      toast.error(t('post.aiWizard.noContentGenerated', 'Please generate a job post first'));
      isSubmittingRef.current = false;
      setPosting(false);
      return;
    }

    // Validate salary range with proper type checking - backend expects integers
    const salaryMin = Math.floor(Number(formData.salary_min));
    const salaryMax = Math.floor(Number(formData.salary_max));
    if (isNaN(salaryMin) || salaryMin <= 0) {
      toast.error(t('post.aiWizard.invalidSalaryMin', 'Please enter a valid minimum salary'));
      isSubmittingRef.current = false;
      setPosting(false);
      return;
    }
    if (isNaN(salaryMax) || salaryMax <= 0) {
      toast.error(t('post.aiWizard.invalidSalaryMax', 'Please enter a valid maximum salary'));
      isSubmittingRef.current = false;
      setPosting(false);
      return;
    }
    if (salaryMin > salaryMax) {
      toast.error(t('post.aiWizard.salaryError', 'Minimum salary cannot be greater than maximum salary'));
      isSubmittingRef.current = false;
      setPosting(false);
      return;
    }
    try {
      // Convert skill values to labels for the job post
      const skillLabels = formData.skills.map(skillValue => {
        const skill = WORKER_SKILLS.find(s => s.value === skillValue);
        return skill ? skill.label : skillValue;
      });

      // Use edited description if user modified it, otherwise use AI-generated
      // Note: Backend performs authoritative XSS sanitization with bleach library
      // This frontend sanitization is defense-in-depth only
      let finalDescription = editedDescription || generatedPost.full_description;
      if (editedDescription) {
        // Defense-in-depth XSS prevention (backend sanitizes with bleach)
        finalDescription = editedDescription
          // Remove script tags
          .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
          // Remove event handlers (onclick, onerror, onload, etc.)
          .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
          .replace(/\s*on\w+\s*=[^\s>]*/gi, '')
          // Remove javascript: URLs
          .replace(/javascript\s*:/gi, '')
          // Remove data: URLs that could contain scripts
          .replace(/data\s*:\s*text\/html/gi, '');
      }

      // Prepare the final job data with all required fields
      const jobData = {
        title: editedTitle || generatedPost.generated_title || formData.job_title,
        description: finalDescription,
        category_id: formData.category_id,
        locations: formData.locations,
        type: 'permanent', // Always permanent now (seasonal/contract removed)
        work_schedule: formData.position_type, // full_time or part_time
        salary_min: salaryMin,
        salary_max: salaryMax,
        salary_period: formData.salary_period,
        requirements: editedRequirements
          ? editedRequirements.split('\n').map(r => r.replace(/^[•\-\*]\s*/, '').trim()).filter(r => r)
          : (typeof generatedPost.requirements === 'string' && generatedPost.requirements)
            ? generatedPost.requirements.split('\n').map(r => r.trim()).filter(r => r)
            : (Array.isArray(generatedPost.requirements) ? generatedPost.requirements.map(r => String(r).trim()).filter(r => r) : []),
        benefits: formData.benefits,
        image_url: customImage || jobImage?.image_url || null,
        image_position: imagePosition,
        urgent_hiring: formData.urgent_hiring,
        no_experience_needed: formData.no_experience_needed || formData.experience_level === 'none',
        with_housing: formData.with_housing || formData.benefits.includes('Free Accommodation'),
        custom_question: formData.custom_question || null,
        required_documents: formData.required_documents.length > 0 ? formData.required_documents : null,
        positions_available: formData.positions_available || 1,
        is_featured: isFeatured,
        post_type: 'job' // Explicitly mark as job post
      };

      await onComplete(jobData);
    } catch (error) {
      console.error('Error posting job:', error);
      toast.error(t('post.aiWizard.postFailed', 'Failed to post job. Please try again.'));
    } finally {
      isSubmittingRef.current = false;
      setPosting(false);
    }
  };

  // Get unique barangays with index-based keys to avoid duplicates
  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderBasicsStep();
      case 1:
        return renderRequirementsStep();
      case 2:
        return renderCompensationStep();
      case 3:
        return renderNotesStep();
      case 4:
        return renderPreviewStep();
      default:
        return null;
    }
  };

  const renderBasicsStep = () => (
    <div className="space-y-6">
      {/* Job Title */}
      <div className="space-y-2">
        <Label htmlFor="job_title" className="text-base font-semibold">
          {t('post.aiWizard.whatPosition', 'What position are you hiring for?')} *
        </Label>
        <Input
          id="job_title"
          placeholder={t('post.aiWizard.jobTitlePlaceholder', 'e.g., Restaurant Server, Construction Worker, Store Cashier')}
          value={formData.job_title}
          onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
          className="rounded-xl text-lg py-6"
        />
      </div>

      {/* Category */}
      <div className="space-y-2">
        <Label className="text-base font-semibold">{t('post.aiWizard.whatCategory', 'What category?')} *</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => handleCategoryChange(cat.id)}
              className={`p-4 rounded-xl border-2 text-left transition-colors ${
                formData.category_id === cat.id
                  ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                  : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
              }`}
            >
              <div className="font-medium">{cat.name}</div>
            </button>
          ))}
        </div>
        {/* Custom category input when "Other" is selected */}
        {formData.category_id && categories.find(c => c.id === formData.category_id)?.name?.toLowerCase().includes('other') && (
          <div className="mt-3 p-4 border-2 border-primary/30 rounded-xl bg-primary/5">
            <Label htmlFor="custom_category" className="text-sm font-medium mb-2 block">
              {t('post.aiWizard.specifyCategory', 'Please specify your category')} *
            </Label>
            <Input
              id="custom_category"
              placeholder={t('post.aiWizard.customCategoryPlaceholder', 'e.g., Pet Grooming, Event Photography, Laundry Services')}
              value={formData.custom_category}
              onChange={(e) => setFormData({ ...formData, custom_category: e.target.value })}
              className="rounded-xl"
            />
          </div>
        )}
      </div>

      {/* Location */}
      <div className="space-y-4 p-4 border rounded-xl bg-gray-50">
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-primary" />
          <Label className="text-base font-semibold">{t('post.aiWizard.whereLocated', 'Where is the job located?')} *</Label>
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="flex w-full items-center justify-between rounded-xl border border-input bg-white px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 min-h-[40px]" data-testid="wizard-locations">
              <span className={formData.locations.length === 0 ? 'text-muted-foreground' : ''}>
                {formData.locations.length === 0 ? t('post.selectLocations', 'Select municipalities') : `${formData.locations.length} selected`}
              </span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50"><path d="m6 9 6 6 6-6"/></svg>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <div className="max-h-[250px] overflow-y-auto p-2" onWheel={(e) => e.stopPropagation()}>
              {BOHOL_MUNICIPALITIES.map((municipality) => (
                <label key={municipality} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm">
                  <Checkbox
                    checked={formData.locations.includes(municipality)}
                    onCheckedChange={(checked) => {
                      const next = checked
                        ? [...formData.locations, municipality]
                        : formData.locations.filter(l => l !== municipality);
                      setFormData({ ...formData, locations: next });
                    }}
                  />
                  {municipality}
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        {formData.locations.length > 0 && (
          <p className="text-xs text-muted-foreground">{formData.locations.join(', ')}</p>
        )}
      </div>

      {/* Number of Positions */}
      <div className="space-y-2">
        <Label htmlFor="positions_available" className="text-base font-semibold">
          How many people are you hiring?
        </Label>
        <Input
          id="positions_available"
          type="number"
          min="1"
          max="100"
          placeholder="1"
          value={formData.positions_available}
          onChange={(e) => {
            const val = e.target.value;
            setFormData({ ...formData, positions_available: val === '' ? '' : Math.max(1, parseInt(val) || 1) });
          }}
          onBlur={() => {
            if (!formData.positions_available) setFormData({ ...formData, positions_available: 1 });
          }}
          onWheel={(e) => e.target.blur()}
          className="rounded-xl w-32"
        />
        <p className="text-xs text-muted-foreground">
          Leave as 1 for single position, or enter more if hiring multiple people
        </p>
      </div>
    </div>
  );

  const renderRequirementsStep = () => (
    <div className="space-y-6">
      {/* Position Type - Simplified: combines employment type and work schedule */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('post.aiWizard.positionType', 'What type of position is this?')} *</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            {
              value: 'full_time',
              label: t('position.fullTime', 'Full-Time Position'),
              icon: '⏰',
              desc: t('position.fullTimeDesc', 'Regular hours (8hrs/day), ongoing employment')
            },
            {
              value: 'part_time',
              label: t('position.partTime', 'Part-Time Position'),
              icon: '⏱️',
              desc: t('position.partTimeDesc', 'Flexible/reduced hours, ongoing employment')
            }
          ].map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() => setFormData({ ...formData, position_type: type.value })}
              className={`p-5 rounded-xl border-2 text-left transition-colors ${
                formData.position_type === type.value
                  ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                  : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">{type.icon}</span>
                <span className="font-semibold text-lg">{type.label}</span>
              </div>
              <div className="text-sm text-muted-foreground">{type.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Experience Level */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('post.aiWizard.experienceRequired', 'Experience Required')}</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { value: 'none', label: t('experience.none', 'No Experience') },
            { value: 'less_than_1', label: t('experience.lessThan1', 'Less than 1 year') },
            { value: '1_2_years', label: t('experience.1to2', '1-2 years') },
            { value: '3_5_years', label: t('experience.3to5', '3-5 years') },
            { value: '5_plus', label: t('experience.5plus', '5+ years') }
          ].map((exp) => (
            <button
              key={exp.value}
              type="button"
              onClick={() => setFormData({ ...formData, experience_level: exp.value })}
              className={`p-3 rounded-xl border-2 text-center transition-colors ${
                formData.experience_level === exp.value
                  ? 'border-primary bg-primary/10'
                  : 'border-gray-200 hover:border-primary/50'
              }`}
            >
              <div className="font-medium text-sm">{exp.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Education Level */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('post.aiWizard.educationRequired', 'Education Required')}</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { value: 'any', label: t('education.any', 'Any Level') },
            { value: 'elementary', label: t('education.elementary', 'Elementary') },
            { value: 'high_school', label: t('education.highSchool', 'High School') },
            { value: 'vocational', label: t('education.vocational', 'Vocational/TESDA') },
            { value: 'college_level', label: t('education.collegeLevel', 'College Level') },
            { value: 'college', label: t('education.college', 'College Graduate') }
          ].map((edu) => (
            <button
              key={edu.value}
              type="button"
              onClick={() => setFormData({ ...formData, education_level: edu.value })}
              className={`p-3 rounded-xl border-2 text-center transition-colors ${
                formData.education_level === edu.value
                  ? 'border-primary bg-primary/10'
                  : 'border-gray-200 hover:border-primary/50'
              }`}
            >
              <div className="font-medium text-sm">{edu.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Skills - Using WORKER_SKILLS from constants */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('post.aiWizard.skillsNeeded', 'Skills Needed')} ({t('common.selectAllApply', 'select all that apply')})</Label>
        <div className="flex flex-wrap gap-2">
          {WORKER_SKILLS.filter(s => s.value !== 'other').map((skill) => (
            <button
              key={skill.value}
              type="button"
              onClick={() => toggleSkill(skill.value)}
              className={`px-4 py-2 rounded-full border-2 text-sm transition-colors ${
                formData.skills.includes(skill.value)
                  ? 'border-primary bg-primary text-white'
                  : 'border-gray-200 hover:border-primary/50'
              }`}
            >
              {formData.skills.includes(skill.value) && <Check className="w-4 h-4 inline mr-1" />}
              {skill.label}
            </button>
          ))}
        </div>
      </div>

      {/* Specific Requirements */}
      <div className="space-y-3 p-4 border-2 border-amber-200 rounded-xl bg-amber-50">
        <Label className="text-base font-semibold flex items-center gap-2">
          <span className="text-amber-600">📋</span>
          {t('post.aiWizard.specificRequirements', 'Specific Requirements')}
        </Label>
        <p className="text-sm text-muted-foreground">
          {t('post.aiWizard.specificRequirementsHint', 'List any must-have requirements for candidates (e.g., must own motorcycle, must have driver\'s license, must be willing to work nights)')}
        </p>
        <Textarea
          placeholder={t('post.aiWizard.specificRequirementsPlaceholder', 'e.g., Must own a motorcycle, Must have valid driver\'s license, Must be willing to work on weekends')}
          value={formData.specific_requirements}
          onChange={(e) => setFormData({ ...formData, specific_requirements: e.target.value.slice(0, 500) })}
          rows={3}
          maxLength={500}
          className="rounded-xl bg-white"
        />
        <p className={`text-xs ${formData.specific_requirements.length > 450 ? 'text-amber-600' : 'text-muted-foreground'}`}>
          {formData.specific_requirements.length}/500 {t('common.characters', 'characters')}
        </p>
      </div>
    </div>
  );

  const renderCompensationStep = () => (
    <div className="space-y-6">
      {/* Salary Range */}
      <div className="space-y-4">
        <Label className="text-base font-semibold">{t('post.aiWizard.salaryRange', 'Salary Range')} *</Label>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">{t('post.minimum', 'Minimum')} (₱)</Label>
            <Input
              type="number"
              placeholder={t('salary.minPlaceholder', 'e.g., 15000')}
              value={formData.salary_min}
              onChange={(e) => setFormData({ ...formData, salary_min: e.target.value })}
              onWheel={(e) => e.target.blur()}
              className="rounded-xl text-lg py-6"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">{t('post.maximum', 'Maximum')} (₱)</Label>
            <Input
              type="number"
              placeholder={t('salary.maxPlaceholder', 'e.g., 25000')}
              value={formData.salary_max}
              onChange={(e) => setFormData({ ...formData, salary_max: e.target.value })}
              onWheel={(e) => e.target.blur()}
              className="rounded-xl text-lg py-6"
            />
          </div>
        </div>

        <div className="flex gap-3">
          {[
            { value: 'daily', label: t('salary.perDay', 'Per Day') },
            { value: 'monthly', label: t('salary.perMonth', 'Per Month') }
          ].map((period) => (
            <button
              key={period.value}
              type="button"
              onClick={() => setFormData({ ...formData, salary_period: period.value })}
              className={`flex-1 p-3 rounded-xl border-2 text-center transition-colors ${
                formData.salary_period === period.value
                  ? 'border-primary bg-primary/10'
                  : 'border-gray-200 hover:border-primary/50'
              }`}
            >
              <div className="font-medium">{period.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Benefits */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">
          <Gift className="w-5 h-5 inline mr-2" />
          {t('post.aiWizard.benefitsPerks', 'Benefits & Perks')}
        </Label>
        <div className="grid grid-cols-2 gap-3">
          {COMMON_BENEFITS.map((benefit) => (
            <button
              key={benefit}
              type="button"
              onClick={() => toggleBenefit(benefit)}
              className={`p-3 rounded-xl border-2 text-left transition-colors flex items-center gap-2 ${
                formData.benefits.includes(benefit)
                  ? 'border-primary bg-primary/10'
                  : 'border-gray-200 hover:border-primary/50'
              }`}
            >
              <span className="font-medium text-sm">{benefit}</span>
              {formData.benefits.includes(benefit) && (
                <Check className="w-4 h-4 ml-auto text-primary" />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderNotesStep = () => (
    <div className="space-y-6">
      {/* Additional Notes */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('post.aiWizard.additionalInfo', 'Any additional information for the AI?')}</Label>
        <p className="text-sm text-muted-foreground">
          {t('post.aiWizard.additionalInfoHint', 'Add any special requirements, company culture details, or specific things you want mentioned in the job post.')}
        </p>
        <Textarea
          placeholder={t('post.aiWizard.additionalInfoPlaceholder', "e.g., We're a family-owned restaurant looking for someone friendly and hardworking. Training provided for beginners.")}
          value={formData.additional_notes}
          onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value.slice(0, 10000) })}
          rows={5}
          maxLength={10000}
          className="rounded-xl"
        />
        <p className={`text-xs ${formData.additional_notes.length > 9500 ? 'text-amber-600' : 'text-muted-foreground'}`}>
          {formData.additional_notes.length}/10000 {t('common.characters', 'characters')}
        </p>
      </div>

      {/* Description Length Toggle */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('post.aiWizard.descriptionLength', 'Description Style')}</Label>
        <p className="text-sm text-muted-foreground">
          {t('post.aiWizard.descriptionLengthHint', 'Choose how long the AI-generated description should be.')}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setDescriptionLength('detailed')}
            className={`p-4 rounded-xl border-2 text-left transition-colors ${
              descriptionLength === 'detailed'
                ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                : 'border-gray-200 hover:border-primary/50'
            }`}
          >
            <div className="font-medium">{t('post.aiWizard.detailed', 'Detailed')}</div>
            <div className="text-xs text-muted-foreground">{t('post.aiWizard.detailedDesc', '3-4 paragraphs, thorough description')}</div>
          </button>
          <button
            type="button"
            onClick={() => setDescriptionLength('concise')}
            className={`p-4 rounded-xl border-2 text-left transition-colors ${
              descriptionLength === 'concise'
                ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                : 'border-gray-200 hover:border-primary/50'
            }`}
          >
            <div className="font-medium">{t('post.aiWizard.concise', 'Concise')}</div>
            <div className="text-xs text-muted-foreground">{t('post.aiWizard.conciseDesc', '2 short paragraphs, straight to the point')}</div>
          </button>
        </div>
      </div>

      {/* Quick Tags */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('post.aiWizard.quickTags', 'Quick Tags')}</Label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => setFormData({ ...formData, urgent_hiring: !formData.urgent_hiring })}
            className={`p-4 rounded-xl border-2 text-left transition-colors ${
              formData.urgent_hiring
                ? 'border-red-500 bg-red-50'
                : 'border-gray-200 hover:border-red-300'
            }`}
          >
            <div className="font-medium">{t('post.aiWizard.urgentHiring', '🔴 Urgent Hiring')}</div>
            <div className="text-xs text-muted-foreground">{t('post.aiWizard.urgentHiringDesc', 'Need to fill quickly')}</div>
          </button>

          <button
            type="button"
            onClick={() => setFormData({ ...formData, no_experience_needed: !formData.no_experience_needed })}
            className={`p-4 rounded-xl border-2 text-left transition-colors ${
              formData.no_experience_needed
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-green-300'
            }`}
          >
            <div className="font-medium">{t('post.aiWizard.beginnersWelcome', '⭐ Beginners Welcome')}</div>
            <div className="text-xs text-muted-foreground">{t('post.aiWizard.beginnersWelcomeDesc', 'Open to fresh grads')}</div>
          </button>

          <button
            type="button"
            onClick={() => setFormData({ ...formData, with_housing: !formData.with_housing })}
            className={`p-4 rounded-xl border-2 text-left transition-colors ${
              formData.with_housing
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <div className="font-medium">{t('post.aiWizard.withHousing', '🏠 With Housing')}</div>
            <div className="text-xs text-muted-foreground">{t('post.aiWizard.withHousingDesc', 'Accommodation included')}</div>
          </button>
        </div>
      </div>

      {/* Custom Screening Question */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('post.aiWizard.screeningQuestion', 'Screening Question (Optional)')}</Label>
        <p className="text-sm text-muted-foreground">
          {t('post.aiWizard.screeningQuestionHint', 'Add a question that applicants must answer when applying. Their answer will be visible when reviewing applicants.')}
        </p>
        <Textarea
          placeholder={t('post.aiWizard.screeningQuestionPlaceholder', "e.g., Why are you interested in this position? / Do you have your own transportation?")}
          value={formData.custom_question}
          onChange={(e) => setFormData({ ...formData, custom_question: e.target.value.slice(0, 500) })}
          rows={3}
          maxLength={500}
          className="rounded-xl"
        />
        <p className={`text-xs ${formData.custom_question.length > 450 ? 'text-amber-600' : 'text-muted-foreground'}`}>
          {formData.custom_question.length}/500 {t('common.characters', 'characters')}
        </p>
      </div>

      {/* Required Documents */}
      <div className="space-y-3">
        <Label className="text-base font-semibold flex items-center gap-2">
          <Paperclip className="w-5 h-5" />
          {t('post.aiWizard.requiredDocuments', 'Required Documents (Optional)')}
        </Label>
        <p className="text-sm text-muted-foreground">
          {t('jobDocs.formHint', "List the documents you'll need. For each one choose \"To apply\" (they must upload it before sending the application) or \"If hired\" (they can apply now and bring it later).")}
        </p>

        {/* Application-impact warning — required docs block applying */}
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-900">
            <span className="font-semibold">{t('post.aiWizard.docsWarnTitle', 'Heads up:')}</span>{' '}
            {t('jobDocs.warnBody', "Job seekers can't apply until they upload every document marked \"To apply\". Asking for clearances up front is the #1 reason jobs get zero applicants — mark NBI, medical, birth certificate, TIN and similar as \"If hired\". You'll still see what each applicant hasn't sent and can ask for it in chat.")}
          </div>
        </div>

        {/* Quick-add common documents */}
        <div className="flex flex-wrap gap-2">
          {[
            { name: 'Resume', label: t('documents.resume', 'Resume') },
            { name: 'Police Clearance', label: t('documents.policeClearance', 'Police Clearance') },
            { name: 'NBI Clearance', label: t('documents.nbiClearance', 'NBI Clearance') },
            { name: 'Barangay Clearance', label: t('documents.barangayClearance', 'Barangay Clearance') },
            { name: 'Medical Certificate', label: t('documents.medicalCertificate', 'Medical Certificate') },
            { name: 'Valid ID', label: t('documents.validId', 'Valid ID') },
            { name: "Driver's License", label: t('documents.driversLicense', "Driver's License") },
            { name: 'SSS ID/E1', label: t('documents.sssId', 'SSS ID/E1') },
            { name: 'PhilHealth ID', label: t('documents.philhealthId', 'PhilHealth ID') },
            { name: 'TIN', label: t('documents.tin', 'TIN') }
          ].map((doc) => {
            const isSelected = formData.required_documents.some(d => d.name === doc.name);
            return (
              <button
                key={doc.name}
                type="button"
                onClick={() => {
                  if (isSelected) {
                    setFormData(prev => ({
                      ...prev,
                      required_documents: prev.required_documents.filter(d => d.name !== doc.name)
                    }));
                  } else {
                    setFormData(prev => ({
                      ...prev,
                      required_documents: [...prev.required_documents, newJobDocument(doc.name)]
                    }));
                  }
                }}
                className={`px-3 py-1.5 rounded-full text-sm border-2 transition-colors ${
                  isSelected
                    ? 'border-primary bg-primary text-white'
                    : 'border-gray-200 hover:border-primary/50'
                }`}
              >
                {isSelected && <Check className="w-3 h-3 inline mr-1" />}
                {doc.label}
              </button>
            );
          })}
        </div>

        {/* Custom document input */}
        <div className="flex gap-2">
          <Input
            placeholder={t('post.aiWizard.customDocumentPlaceholder', 'Add custom document requirement...')}
            className="rounded-xl"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target.value.trim()) {
                e.preventDefault();
                const newDoc = e.target.value.trim();
                if (!formData.required_documents.some(d => d.name.toLowerCase() === newDoc.toLowerCase())) {
                  setFormData(prev => ({
                    ...prev,
                    required_documents: [...prev.required_documents, newJobDocument(newDoc)]
                  }));
                }
                e.target.value = '';
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="rounded-xl"
            onClick={(e) => {
              const input = e.target.closest('.flex').querySelector('input');
              if (input && input.value.trim()) {
                const newDoc = input.value.trim();
                if (!formData.required_documents.some(d => d.name.toLowerCase() === newDoc.toLowerCase())) {
                  setFormData(prev => ({
                    ...prev,
                    required_documents: [...prev.required_documents, newJobDocument(newDoc)]
                  }));
                }
                input.value = '';
              }
            }}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {/* Selected documents list */}
        {formData.required_documents.length > 0 && (
          <div className="p-3 bg-primary/5 rounded-xl border border-primary/20">
            <p className="text-sm font-medium mb-2">{t('post.aiWizard.selectedDocuments', 'Selected Documents')} ({formData.required_documents.length}):</p>
            <div className="space-y-2">
              {formData.required_documents.map((doc, index) => (
                <div
                  key={index}
                  className="flex flex-wrap items-center gap-2 px-2 py-1.5 bg-white rounded-lg text-sm border"
                >
                  <FileText className="w-4 h-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 min-w-[7.5rem] break-words">{doc.name}</span>
                  <DocumentNeedToggle
                    doc={doc}
                    onChange={(updated) => {
                      setFormData(prev => ({
                        ...prev,
                        required_documents: prev.required_documents.map((d, i) => (i === index ? updated : d))
                      }));
                    }}
                  />
                  <button
                    type="button"
                    aria-label={t('jobDocs.remove', 'Remove {{name}}', { name: doc.name })}
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        required_documents: prev.required_documents.filter((_, i) => i !== index)
                      }));
                    }}
                    className="min-w-[36px] min-h-[36px] inline-flex items-center justify-center text-muted-foreground hover:text-destructive"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="space-y-6">
      {generatedPost ? (
        <>
          {/* Generated Title - Editable */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">{t('post.aiWizard.generatedTitle', 'Generated Title')}</Label>
            <textarea
              value={editedTitle}
              onChange={(e) => {
                setEditedTitle(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              ref={(el) => {
                if (el) {
                  el.style.height = 'auto';
                  el.style.height = el.scrollHeight + 'px';
                }
              }}
              rows={1}
              className="w-full rounded-xl text-xl font-bold py-3 px-3 border-2 border-primary/20 bg-primary/5 resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              placeholder={generatedPost.generated_title || formData.job_title}
            />
          </div>

          {/* Job Image */}
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="text-base font-semibold">
                <ImageIcon className="w-5 h-5 inline mr-2" />
                {t('post.aiWizard.jobImage', 'Job Image')}
              </Label>
              <div className="flex gap-2">
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                {/* Upload own image button */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="rounded-xl"
                >
                  {uploadingImage ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {t('post.aiWizard.uploadImage', 'Upload Own')}
                </Button>
                {/* Get different AI image button - only show if no custom image */}
                {!customImage && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={refreshImage}
                    disabled={imageLoading}
                    className="rounded-xl"
                  >
                    {imageLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    {t('post.aiWizard.getDifferentImage', 'Get Different')}
                  </Button>
                )}
              </div>
            </div>

            {/* Image display - priority: custom > AI-generated > fallback */}
            {customImage ? (
              <div className="relative rounded-xl overflow-hidden border">
                <img
                  src={customImage}
                  alt="Custom job image"
                  className="w-full h-48 object-cover"
                  style={{ objectPosition: `center ${imagePosition}%` }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800';
                  }}
                />
                {/* Remove custom image button */}
                <button
                  type="button"
                  onClick={removeCustomImage}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg transition-colors"
                  title={t('post.aiWizard.removeImage', 'Remove image')}
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-2">
                  {t('post.aiWizard.customImage', 'Your uploaded image')}
                </div>
              </div>
            ) : jobImage?.image_url ? (
              <div className="relative rounded-xl overflow-hidden border">
                <img
                  src={jobImage.image_url}
                  alt="Job"
                  className="w-full h-48 object-cover"
                  style={{ objectPosition: `center ${imagePosition}%` }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800';
                  }}
                />
                {jobImage.photographer && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-2">
                    Photo by {jobImage.photographer} on Unsplash
                  </div>
                )}
              </div>
            ) : (
              <div className="relative rounded-xl overflow-hidden border">
                <img
                  src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800"
                  alt="Professional workplace"
                  className="w-full h-48 object-cover"
                  style={{ objectPosition: `center ${imagePosition}%` }}
                />
                {imageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Loader2 className="w-8 h-8 animate-spin text-white" />
                  </div>
                )}
              </div>
            )}
            {/* Image Position Slider */}
            {(customImage || jobImage?.image_url) && (
              <div className="flex items-center gap-3 mt-2">
                <Label className="text-sm text-muted-foreground whitespace-nowrap">{t('post.aiWizard.imagePosition', 'Image Position')}</Label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={imagePosition}
                  onChange={(e) => setImagePosition(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg cursor-pointer accent-primary"
                />
              </div>
            )}
          </div>

          {/* Generated Description - Editable */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">{t('post.aiWizard.generatedDescription', 'Generated Description')}</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (editingDescription) {
                    // Save changes
                    setEditingDescription(false);
                  } else {
                    // Enter edit mode - initialize with current content
                    setEditedDescription(editedDescription || generatedPost.full_description);
                    setEditingDescription(true);
                  }
                }}
                className="rounded-xl"
              >
                {editingDescription ? (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {t('common.save', 'Save')}
                  </>
                ) : (
                  <>
                    <Edit className="w-4 h-4 mr-2" />
                    {t('common.edit', 'Edit')}
                  </>
                )}
              </Button>
            </div>
            {editingDescription ? (
              <RichTextEditor
                value={editedDescription}
                onChange={(value) => setEditedDescription(value)}
                placeholder={t('post.aiWizard.editDescriptionPlaceholder', 'Edit the job description...')}
                className="rounded-xl"
              />
            ) : (
              <div
                className="p-4 bg-gray-50 rounded-xl border prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(editedDescription || generatedPost.full_description || '') }}
              />
            )}
          </div>

          {/* AI-Generated Requirements - Editable */}
          {generatedPost.requirements && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">📋 {t('post.aiWizard.requirements', 'Requirements')}</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (editingRequirements) {
                      setEditingRequirements(false);
                    } else {
                      const reqs = editedRequirements || (typeof generatedPost.requirements === 'string'
                        ? generatedPost.requirements.split('\n').map(r => r.replace(/^[•\-\*]\s*/, '').trim()).filter(r => r).join('\n')
                        : Array.isArray(generatedPost.requirements)
                          ? generatedPost.requirements.map(r => r.replace(/^[•\-\*]\s*/, '').trim()).filter(r => r).join('\n')
                          : '');
                      setEditedRequirements(reqs);
                      setEditingRequirements(true);
                    }
                  }}
                  className="rounded-xl"
                >
                  {editingRequirements ? (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t('common.save', 'Save')}
                    </>
                  ) : (
                    <>
                      <Edit className="w-4 h-4 mr-2" />
                      {t('common.edit', 'Edit')}
                    </>
                  )}
                </Button>
              </div>
              {editingRequirements ? (
                <textarea
                  value={editedRequirements}
                  onChange={(e) => setEditedRequirements(e.target.value)}
                  rows={6}
                  className="w-full rounded-xl p-4 border border-amber-200 bg-amber-50 text-sm text-amber-900 resize-y focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  placeholder={t('post.aiWizard.editRequirementsPlaceholder', 'One requirement per line...')}
                />
              ) : (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {(editedRequirements
                      ? editedRequirements.split('\n').map(r => r.trim()).filter(r => r)
                      : (typeof generatedPost.requirements === 'string'
                        ? generatedPost.requirements.split('\n').map(r => r.trim()).filter(r => r)
                        : Array.isArray(generatedPost.requirements) ? generatedPost.requirements : [])
                    ).map((req, index) => (
                      <li key={index} className="text-amber-900">
                        {req.replace(/^[•\-\*]\s*/, '')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Selected Benefits - Displayed separately from AI content */}
          {formData.benefits.length > 0 && (
            <div className="space-y-2">
              <Label className="text-base font-semibold">🎁 {t('post.aiWizard.selectedBenefits', 'Benefits & Perks')}</Label>
              <div className="flex flex-wrap gap-2 p-4 bg-green-50 rounded-xl border border-green-200">
                {formData.benefits.map(benefit => (
                  <span
                    key={benefit}
                    className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium"
                  >
                    {benefit}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Selected Skills */}
          {formData.skills.length > 0 && (
            <div className="space-y-2">
              <Label className="text-base font-semibold">🎯 {t('post.aiWizard.skillsRequired', 'Skills Required')}</Label>
              <div className="flex flex-wrap gap-2">
                {formData.skills.map(skillValue => {
                  const skill = WORKER_SKILLS.find(s => s.value === skillValue);
                  return (
                    <span
                      key={skillValue}
                      className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                    >
                      {skill ? skill.label : skillValue}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Required Documents */}
          {formData.required_documents.length > 0 && (
            <div className="space-y-2">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Paperclip className="w-5 h-5" />
                {t('post.aiWizard.requiredDocuments', 'Required Documents')}
              </Label>
              <div className="flex flex-wrap gap-2 p-4 bg-orange-50 rounded-xl border border-orange-200">
                {formData.required_documents.map((doc, index) => (
                  <span
                    key={index}
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                      neededToApply(doc) ? 'bg-orange-100 text-orange-800' : 'bg-emerald-100 text-emerald-800'
                    }`}
                  >
                    <FileText className="w-3 h-3" />
                    {doc.name}
                    <span className="text-xs font-normal opacity-80">
                      · {neededToApply(doc) ? t('jobDocs.toApply', 'To apply') : t('jobDocs.ifHired', 'If hired')}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Screening Question */}
          {formData.custom_question && (
            <div className="space-y-2">
              <Label className="text-base font-semibold">❓ {t('post.aiWizard.screeningQuestion', 'Screening Question')}</Label>
              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200 text-sm text-purple-900">
                {formData.custom_question}
              </div>
            </div>
          )}

          {/* Salary Info */}
          <div className="p-4 bg-green-50 rounded-xl border border-green-200">
            <div className="font-semibold text-green-800">
              💰 {t('post.salary', 'Salary')}: ₱{(parseFloat(formData.salary_min) || 0).toLocaleString()} - ₱{(parseFloat(formData.salary_max) || 0).toLocaleString()}
              {formData.salary_period === 'daily' ? ` ${t('salary.perDay', 'per day')}` : ` ${t('salary.perMonth', 'per month')}`}
            </div>
          </div>

          {/* Regenerate Button */}
          <div className="flex justify-center">
            <Button
              type="button"
              variant="outline"
              onClick={generateJobPost}
              disabled={generating}
              className="rounded-xl"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {t('post.aiWizard.regenerating', 'Regenerating...')}
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('post.aiWizard.regenerate', 'Regenerate Post')}
                </>
              )}
            </Button>
          </div>

          {/* Feature Option */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-secondary/50 transition-colors">
              <Checkbox
                id="ai-wizard-featured"
                checked={isFeatured}
                onCheckedChange={(checked) => setIsFeatured(checked === true)}
                disabled={credits < (pricing.featured_post || 30)}
              />
              <div className="flex-1">
                <label htmlFor="ai-wizard-featured" className="text-sm font-medium cursor-pointer flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500" />
                  Feature this posting ({pricing.featured_post || 30} credits)
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  Featured postings appear at the top for 7 days. Your balance: {credits} credits
                </p>
                {credits < (pricing.featured_post || 30) && (
                  <p className="text-xs text-red-500 mt-1">Insufficient credits.</p>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
          <p className="text-lg font-medium">{t('post.aiWizard.generatingPost', 'Generating your job post...')}</p>
          <p className="text-muted-foreground">{t('post.aiWizard.aiCrafting', 'Our AI is crafting the perfect job listing')}</p>
        </div>
      )}
    </div>
  );

  return (
    <Card className="border-2">
      <CardHeader className="border-b bg-gradient-to-r from-primary/5 to-primary/10">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary rounded-xl">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl">{t('post.aiWizard.title', 'AI Job Wizard')}</CardTitle>
            <CardDescription>{t('post.aiWizard.subtitle', 'Answer a few questions and let AI create your job post')}</CardDescription>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mt-6 overflow-x-auto pb-2">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`flex items-center ${index < steps.length - 1 ? 'flex-1' : ''}`}
            >
              <div className={`flex flex-col items-center ${index <= currentStep ? 'text-primary' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                  index < currentStep
                    ? 'bg-primary border-primary text-white'
                    : index === currentStep
                      ? 'border-primary text-primary'
                      : 'border-gray-300'
                }`}>
                  {index < currentStep ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <span className="text-xs mt-1 hidden md:block whitespace-nowrap">{step.title}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${index < currentStep ? 'bg-primary' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>
      </CardHeader>

      <CardContent className="p-6">
        {/* Step Title */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            {React.createElement(steps[currentStep].icon, { className: 'w-5 h-5' })}
            {steps[currentStep].title}
          </h3>
          <p className="text-muted-foreground">{steps[currentStep].description}</p>
        </div>

        {/* Step Content */}
        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={currentStep === 0 ? onCancel : handleBack}
              className="rounded-xl"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {currentStep === 0 ? t('common.cancel', 'Cancel') : t('post.aiWizard.back', 'Back')}
            </Button>
            {onSaveDraft && formData.job_title?.trim() && (
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveDraft}
                disabled={autoSaving}
                className="rounded-xl"
              >
                {autoSaving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('postJob.saving', 'Saving...')}</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />{t('postJob.saveDraft', 'Save Draft')}</>
                )}
              </Button>
            )}
          </div>

          {currentStep === steps.length - 1 ? (
            <Button
              type="button"
              onClick={handleComplete}
              disabled={!generatedPost || posting}
              className="rounded-xl bg-green-600 hover:bg-green-700"
            >
              {posting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('post.aiWizard.posting', 'Posting...')}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {t('post.aiWizard.postThisJob', 'Post This Job')}
                </>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleNext}
              disabled={generating}
              className="rounded-xl"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {t('post.aiWizard.generating', 'Generating...')}
                </>
              ) : currentStep === 3 ? (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('post.aiWizard.generateJobPost', 'Generate Job Post')}
                </>
              ) : (
                <>
                  {t('post.aiWizard.next', 'Next')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AIJobWizard;
